import { useRef, useState, useEffect } from 'react'
import { motion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion'
import GlowButton from '../shared/GlowButton'
import GradientText from '../shared/GradientText'
import AnimatedCounter from '../shared/AnimatedCounter'
import './HeroSection.css'

// DJI Drone image URL (transparent background)
const DRONE_IMAGE = 'https://images.unsplash.com/photo-1473968512647-3e447244af8f?w=300&h=200&fit=crop&crop=center&auto=format'

// Realistic Satellite SVG Component (Landsat-style observation satellite)
const SatelliteSVG = () => (
  <svg viewBox="0 0 200 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="satellite-svg">
    {/* Left Solar Panel */}
    <g className="solar-panel-left">
      {/* Panel Frame */}
      <rect x="5" y="45" width="55" height="30" rx="2" fill="#1a3a5c" stroke="#4a6a8c" strokeWidth="1"/>
      {/* Solar Cells Grid */}
      {[0,1,2,3,4].map((col) => (
        [0,1,2].map((row) => (
          <rect
            key={`left-${col}-${row}`}
            x={8 + col * 10}
            y={48 + row * 9}
            width="8"
            height="7"
            fill="#0d2847"
            stroke="#2a4a6c"
            strokeWidth="0.5"
          />
        ))
      ))}
      {/* Panel Connector */}
      <rect x="60" y="55" width="12" height="10" fill="#6b7280" stroke="#9ca3af" strokeWidth="0.5"/>
    </g>

    {/* Right Solar Panel */}
    <g className="solar-panel-right">
      {/* Panel Frame */}
      <rect x="140" y="45" width="55" height="30" rx="2" fill="#1a3a5c" stroke="#4a6a8c" strokeWidth="1"/>
      {/* Solar Cells Grid */}
      {[0,1,2,3,4].map((col) => (
        [0,1,2].map((row) => (
          <rect
            key={`right-${col}-${row}`}
            x={143 + col * 10}
            y={48 + row * 9}
            width="8"
            height="7"
            fill="#0d2847"
            stroke="#2a4a6c"
            strokeWidth="0.5"
          />
        ))
      ))}
      {/* Panel Connector */}
      <rect x="128" y="55" width="12" height="10" fill="#6b7280" stroke="#9ca3af" strokeWidth="0.5"/>
    </g>

    {/* Main Satellite Body */}
    <g className="satellite-body">
      {/* Main Bus (rectangular body) */}
      <rect x="72" y="35" width="56" height="50" rx="3" fill="#d1d5db" stroke="#9ca3af" strokeWidth="1"/>
      {/* Body Details - Panels/Vents */}
      <rect x="76" y="40" width="20" height="18" rx="1" fill="#e5e7eb" stroke="#9ca3af" strokeWidth="0.5"/>
      <rect x="76" y="62" width="20" height="18" rx="1" fill="#374151" stroke="#4b5563" strokeWidth="0.5"/>
      <rect x="100" y="40" width="24" height="40" rx="1" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="0.5"/>
      {/* Thermal Blanket Texture */}
      <line x1="102" y1="45" x2="122" y2="45" stroke="#e5e7eb" strokeWidth="0.3"/>
      <line x1="102" y1="50" x2="122" y2="50" stroke="#e5e7eb" strokeWidth="0.3"/>
      <line x1="102" y1="55" x2="122" y2="55" stroke="#e5e7eb" strokeWidth="0.3"/>
      <line x1="102" y1="60" x2="122" y2="60" stroke="#e5e7eb" strokeWidth="0.3"/>
      <line x1="102" y1="65" x2="122" y2="65" stroke="#e5e7eb" strokeWidth="0.3"/>
      <line x1="102" y1="70" x2="122" y2="70" stroke="#e5e7eb" strokeWidth="0.3"/>
      <line x1="102" y1="75" x2="122" y2="75" stroke="#e5e7eb" strokeWidth="0.3"/>
    </g>

    {/* Antenna Dish */}
    <g className="antenna">
      <ellipse cx="85" cy="25" rx="12" ry="8" fill="#e5e7eb" stroke="#9ca3af" strokeWidth="1"/>
      <ellipse cx="85" cy="25" rx="8" ry="5" fill="#f9fafb" stroke="#d1d5db" strokeWidth="0.5"/>
      <line x1="85" y1="33" x2="85" y2="35" stroke="#6b7280" strokeWidth="2"/>
      {/* Antenna Feed */}
      <circle cx="85" cy="25" r="2" fill="#374151"/>
    </g>

    {/* Sensor/Camera Array (bottom) */}
    <g className="sensors">
      <rect x="80" y="85" width="40" height="15" rx="2" fill="#1f2937" stroke="#374151" strokeWidth="1"/>
      {/* Camera Lenses */}
      <circle cx="90" cy="92" r="4" fill="#0f172a" stroke="#334155" strokeWidth="0.5"/>
      <circle cx="90" cy="92" r="2" fill="#1e3a5f"/>
      <circle cx="105" cy="92" r="4" fill="#0f172a" stroke="#334155" strokeWidth="0.5"/>
      <circle cx="105" cy="92" r="2" fill="#1e3a5f"/>
    </g>

    {/* Small Antennas/Instruments */}
    <line x1="72" y1="45" x2="65" y2="38" stroke="#6b7280" strokeWidth="1.5"/>
    <circle cx="63" cy="36" r="2" fill="#9ca3af"/>
    <line x1="128" y1="45" x2="135" y2="38" stroke="#6b7280" strokeWidth="1.5"/>
    <circle cx="137" cy="36" r="2" fill="#9ca3af"/>
  </svg>
)

// Smart Sensor Box SVG Component - Industrial IoT monitoring hub with panel connections
const SmartSensorBoxSVG = () => (
  <svg viewBox="0 0 160 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="sensor-box-svg">
    {/* Connection cables running straight DOWN to panel below */}
    <g className="sensor-cables">
      {/* Cable 1 - goes straight down */}
      <path d="M60 63 L60 85 Q60 95 55 105 L55 195" stroke="#F97316" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
      <path d="M60 63 L60 85 Q60 95 55 105 L55 195" stroke="#FDBA74" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeDasharray="4 2" opacity="0.5"/>

      {/* Cable 2 - middle cable going down */}
      <path d="M70 63 L70 90 Q70 100 72 110 L72 195" stroke="#F97316" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M70 63 L70 90 Q70 100 72 110 L72 195" stroke="#FDBA74" strokeWidth="1" fill="none" strokeLinecap="round" strokeDasharray="3 2" opacity="0.4"/>

      {/* Cable 3 - third cable going down */}
      <path d="M80 63 L80 80 Q80 90 85 100 L85 195" stroke="#F97316" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M80 63 L80 80 Q80 90 85 100 L85 195" stroke="#FDBA74" strokeWidth="1" fill="none" strokeLinecap="round" strokeDasharray="3 2" opacity="0.4"/>

      {/* Cable connectors at bottom of box */}
      <rect x="57" y="57" width="6" height="6" rx="1" fill="#374151"/>
      <rect x="58" y="58" width="4" height="4" rx="0.5" fill="#F97316"/>
      <rect x="67" y="57" width="6" height="6" rx="1" fill="#374151"/>
      <rect x="68" y="58" width="4" height="4" rx="0.5" fill="#F97316"/>
      <rect x="77" y="57" width="6" height="6" rx="1" fill="#374151"/>
      <rect x="78" y="58" width="4" height="4" rx="0.5" fill="#F97316"/>
    </g>

    {/* Main enclosure box - centered */}
    <g className="sensor-enclosure">
      {/* Box shadow */}
      <rect x="42" y="17" width="76" height="48" rx="4" fill="#1F2937" opacity="0.5"/>

      {/* Main box body */}
      <rect x="40" y="13" width="80" height="50" rx="5" fill="url(#boxGradient)" stroke="#4B5563" strokeWidth="1"/>

      {/* Box front panel - recessed area */}
      <rect x="44" y="17" width="72" height="42" rx="3" fill="#1F2937"/>

      {/* Display screen */}
      <rect x="48" y="21" width="40" height="22" rx="2" fill="#0F172A" stroke="#374151" strokeWidth="0.5"/>
      {/* Screen content - waveform/data */}
      <path d="M52 32 L56 28 L60 34 L64 30 L68 33 L72 27 L76 31 L80 29 L84 32"
            stroke="#22C55E" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Screen text indicators */}
      <rect x="50" y="37" width="12" height="3" rx="0.5" fill="#F97316" opacity="0.8"/>
      <rect x="64" y="37" width="8" height="3" rx="0.5" fill="#22C55E" opacity="0.8"/>
      <rect x="74" y="37" width="10" height="3" rx="0.5" fill="#3B82F6" opacity="0.8"/>

      {/* Status LEDs panel */}
      <rect x="92" y="21" width="20" height="22" rx="2" fill="#111827"/>
      {/* LED 1 - Power (green) */}
      <circle cx="98" cy="27" r="2.5" fill="#166534"/>
      <circle cx="98" cy="27" r="1.8" fill="#22C55E" className="led-glow-green"/>
      {/* LED 2 - Data (orange - active) */}
      <circle cx="106" cy="27" r="2.5" fill="#9A3412"/>
      <circle cx="106" cy="27" r="1.8" fill="#F97316" className="led-glow-orange"/>
      {/* LED 3 - Network (blue) */}
      <circle cx="98" cy="35" r="2.5" fill="#1E40AF"/>
      <circle cx="98" cy="35" r="1.8" fill="#3B82F6" className="led-glow-blue"/>
      {/* LED 4 - Alert (off) */}
      <circle cx="106" cy="35" r="2.5" fill="#374151"/>
      <circle cx="106" cy="35" r="1.2" fill="#6B7280"/>

      {/* Connection ports at bottom */}
      <g className="connection-ports">
        {/* Port 1 */}
        <rect x="48" y="47" width="14" height="8" rx="1" fill="#111827" stroke="#4B5563" strokeWidth="0.5"/>
        <rect x="50" y="49" width="4" height="4" rx="0.5" fill="#F97316"/>
        <rect x="56" y="49" width="4" height="4" rx="0.5" fill="#22C55E"/>

        {/* Port 2 */}
        <rect x="66" y="47" width="14" height="8" rx="1" fill="#111827" stroke="#4B5563" strokeWidth="0.5"/>
        <rect x="68" y="49" width="4" height="4" rx="0.5" fill="#F97316"/>
        <rect x="74" y="49" width="4" height="4" rx="0.5" fill="#22C55E"/>

        {/* Port 3 */}
        <rect x="84" y="47" width="14" height="8" rx="1" fill="#111827" stroke="#4B5563" strokeWidth="0.5"/>
        <rect x="86" y="49" width="4" height="4" rx="0.5" fill="#F97316"/>
        <rect x="92" y="49" width="4" height="4" rx="0.5" fill="#22C55E"/>

        {/* Port 4 - Ethernet */}
        <rect x="102" y="47" width="10" height="8" rx="1" fill="#111827" stroke="#4B5563" strokeWidth="0.5"/>
        <rect x="104" y="49" width="6" height="4" rx="0.5" fill="#3B82F6"/>
      </g>

      {/* Ventilation slots on right side */}
      <g className="vent-slots" opacity="0.6">
        <rect x="116" y="23" width="2" height="6" rx="0.5" fill="#374151"/>
        <rect x="116" y="31" width="2" height="6" rx="0.5" fill="#374151"/>
        <rect x="116" y="39" width="2" height="6" rx="0.5" fill="#374151"/>
      </g>

      {/* Mounting brackets */}
      <rect x="36" y="19" width="4" height="8" rx="1" fill="#6B7280"/>
      <circle cx="38" cy="23" r="1.5" fill="#374151"/>
      <rect x="120" y="19" width="4" height="8" rx="1" fill="#6B7280"/>
      <circle cx="122" cy="23" r="1.5" fill="#374151"/>
      <rect x="36" y="47" width="4" height="8" rx="1" fill="#6B7280"/>
      <circle cx="38" cy="51" r="1.5" fill="#374151"/>
      <rect x="120" y="47" width="4" height="8" rx="1" fill="#6B7280"/>
      <circle cx="122" cy="51" r="1.5" fill="#374151"/>
    </g>

    {/* Antenna on top */}
    <g className="sensor-antenna">
      <rect x="78" y="3" width="3" height="10" rx="1" fill="#6B7280"/>
      <circle cx="79.5" cy="1" r="3" fill="#374151" stroke="#6B7280" strokeWidth="0.5"/>
      <circle cx="79.5" cy="1" r="1.5" fill="#F97316" className="led-glow-orange"/>
    </g>

    {/* Data flow indicators - animated dots on cables going down */}
    <g className="data-flow">
      <circle cx="57" cy="120" r="2" fill="#F97316" className="data-dot data-dot-1"/>
      <circle cx="72" cy="140" r="2" fill="#F97316" className="data-dot data-dot-2"/>
      <circle cx="85" cy="160" r="2" fill="#F97316" className="data-dot data-dot-3"/>
    </g>

    {/* Gradients */}
    <defs>
      <linearGradient id="boxGradient" x1="40" y1="13" x2="40" y2="63" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#4B5563"/>
        <stop offset="50%" stopColor="#374151"/>
        <stop offset="100%" stopColor="#1F2937"/>
      </linearGradient>
    </defs>
  </svg>
)

// Weather Station SVG Component - Professional solar weather monitoring station
const WeatherStationSVG = () => (
  <svg viewBox="0 0 100 140" fill="none" xmlns="http://www.w3.org/2000/svg" className="weather-station-svg">
    {/* Main vertical mast */}
    <rect x="47" y="25" width="6" height="110" fill="url(#mastGradient)" stroke="#9CA3AF" strokeWidth="0.5"/>

    {/* Ground mounting base */}
    <ellipse cx="50" cy="135" rx="18" ry="5" fill="#4B5563"/>
    <rect x="35" y="128" width="30" height="8" rx="2" fill="#6B7280" stroke="#9CA3AF" strokeWidth="0.5"/>

    {/* Anemometer at top - spinning wind cups */}
    <g className="anemometer">
      {/* Center hub */}
      <circle cx="50" cy="12" r="4" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.5"/>
      <circle cx="50" cy="12" r="2" fill="#374151"/>

      {/* Wind cups on arms - 3 cups at 120 degrees */}
      <g className="anemometer-cups">
        {/* Arm 1 - top */}
        <line x1="50" y1="12" x2="50" y2="2" stroke="#9CA3AF" strokeWidth="1.5"/>
        <ellipse cx="50" cy="2" rx="5" ry="3" fill="#D1D5DB" stroke="#9CA3AF" strokeWidth="0.5"/>

        {/* Arm 2 - bottom left */}
        <line x1="50" y1="12" x2="41" y2="20" stroke="#9CA3AF" strokeWidth="1.5"/>
        <ellipse cx="38" cy="21" rx="5" ry="3" fill="#D1D5DB" stroke="#9CA3AF" strokeWidth="0.5" transform="rotate(-30 38 21)"/>

        {/* Arm 3 - bottom right */}
        <line x1="50" y1="12" x2="59" y2="20" stroke="#9CA3AF" strokeWidth="1.5"/>
        <ellipse cx="62" cy="21" rx="5" ry="3" fill="#D1D5DB" stroke="#9CA3AF" strokeWidth="0.5" transform="rotate(30 62 21)"/>
      </g>
    </g>

    {/* Wind vane below anemometer */}
    <g className="wind-vane">
      <rect x="48" y="22" width="4" height="8" fill="#6B7280"/>
      <polygon points="50,18 45,25 55,25" fill="#F59E0B"/>
      <rect x="46" y="26" width="8" height="2" fill="#9CA3AF"/>
    </g>

    {/* Pyranometer - Solar radiation sensor (glass dome) */}
    <g className="pyranometer">
      {/* Mounting arm */}
      <rect x="53" y="42" width="20" height="3" fill="#6B7280"/>
      {/* Sensor base */}
      <ellipse cx="75" cy="43" rx="8" ry="4" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.5"/>
      {/* Glass dome */}
      <ellipse cx="75" cy="41" rx="6" ry="5" fill="url(#glassDome)" stroke="#D1D5DB" strokeWidth="0.5"/>
      {/* Sensor element inside */}
      <circle cx="75" cy="42" r="3" fill="#1F2937"/>
      <circle cx="75" cy="42" r="1.5" fill="#374151"/>
    </g>

    {/* Temperature & Humidity sensor housing (white radiation shield) */}
    <g className="temp-humidity-sensor">
      {/* Mounting bracket */}
      <rect x="25" y="55" width="22" height="3" fill="#6B7280"/>
      {/* Radiation shield - stacked white plates */}
      <rect x="12" y="50" width="16" height="3" rx="1" fill="#F9FAFB" stroke="#E5E7EB" strokeWidth="0.3"/>
      <rect x="12" y="54" width="16" height="3" rx="1" fill="#F9FAFB" stroke="#E5E7EB" strokeWidth="0.3"/>
      <rect x="12" y="58" width="16" height="3" rx="1" fill="#F9FAFB" stroke="#E5E7EB" strokeWidth="0.3"/>
      <rect x="12" y="62" width="16" height="3" rx="1" fill="#F9FAFB" stroke="#E5E7EB" strokeWidth="0.3"/>
      <rect x="12" y="66" width="16" height="3" rx="1" fill="#F9FAFB" stroke="#E5E7EB" strokeWidth="0.3"/>
      {/* Sensor inside */}
      <rect x="17" y="55" width="6" height="12" fill="#374151"/>
    </g>

    {/* Rain gauge */}
    <g className="rain-gauge">
      {/* Mounting arm */}
      <rect x="53" y="72" width="15" height="2" fill="#6B7280"/>
      {/* Funnel */}
      <path d="M66 65 L66 72 L78 72 L78 65 L74 72 L70 72 Z" fill="#E5E7EB" stroke="#9CA3AF" strokeWidth="0.5"/>
      {/* Collection cylinder */}
      <rect x="68" y="72" width="8" height="12" rx="1" fill="#D1D5DB" stroke="#9CA3AF" strokeWidth="0.5"/>
      {/* Water level indicator */}
      <rect x="69" y="78" width="6" height="5" fill="#60A5FA" opacity="0.6"/>
    </g>

    {/* Small solar panel for power */}
    <g className="station-solar-panel">
      {/* Panel frame - angled */}
      <rect x="20" y="88" width="20" height="14" rx="1" fill="#1E3A5F" stroke="#374151" strokeWidth="0.5" transform="rotate(-15 30 95)"/>
      {/* Solar cells */}
      <rect x="22" y="90" width="7" height="4" fill="#0D2847" stroke="#1E3A5F" strokeWidth="0.3" transform="rotate(-15 25 92)"/>
      <rect x="30" y="88" width="7" height="4" fill="#0D2847" stroke="#1E3A5F" strokeWidth="0.3" transform="rotate(-15 33 90)"/>
      <rect x="22" y="96" width="7" height="4" fill="#0D2847" stroke="#1E3A5F" strokeWidth="0.3" transform="rotate(-15 25 98)"/>
      <rect x="30" y="94" width="7" height="4" fill="#0D2847" stroke="#1E3A5F" strokeWidth="0.3" transform="rotate(-15 33 96)"/>
    </g>

    {/* Data logger / communication box */}
    <g className="data-logger">
      {/* Main box */}
      <rect x="55" y="95" width="25" height="20" rx="2" fill="url(#loggerGradient)" stroke="#4B5563" strokeWidth="0.5"/>
      {/* Screen/display */}
      <rect x="58" y="98" width="12" height="8" rx="1" fill="#0F172A"/>
      {/* Status LEDs */}
      <circle cx="74" cy="101" r="1.5" fill="#22C55E" className="led-glow-green"/>
      <circle cx="74" cy="106" r="1.5" fill="#3B82F6" className="led-glow-blue"/>
      {/* Antenna */}
      <rect x="77" y="88" width="2" height="10" fill="#6B7280"/>
      <circle cx="78" cy="86" r="2" fill="#374151"/>
      <circle cx="78" cy="86" r="1" fill="#F59E0B" className="led-glow-orange"/>
    </g>

    {/* Cable running down mast */}
    <path d="M52 115 Q55 118 53 122 Q51 126 53 130" stroke="#374151" strokeWidth="1.5" fill="none"/>

    {/* Gradients */}
    <defs>
      <linearGradient id="mastGradient" x1="47" y1="25" x2="53" y2="25" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#9CA3AF"/>
        <stop offset="50%" stopColor="#D1D5DB"/>
        <stop offset="100%" stopColor="#9CA3AF"/>
      </linearGradient>
      <radialGradient id="glassDome" cx="50%" cy="30%" r="70%">
        <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.9"/>
        <stop offset="50%" stopColor="#E0F2FE" stopOpacity="0.7"/>
        <stop offset="100%" stopColor="#BAE6FD" stopOpacity="0.5"/>
      </radialGradient>
      <linearGradient id="loggerGradient" x1="55" y1="95" x2="55" y2="115" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#4B5563"/>
        <stop offset="100%" stopColor="#1F2937"/>
      </linearGradient>
    </defs>
  </svg>
)

// Floating Weather Station component
const FloatingWeatherStation = ({ position, delay, duration }) => {
  return (
    <motion.div
      className="floating-object floating-weather-station"
      style={{
        '--float-color': '#60A5FA',
        top: position.top,
        left: position.left,
        right: position.right,
        bottom: position.bottom,
      }}
      initial={{ opacity: 0, x: 200, scale: 0.5 }}
      animate={{
        opacity: 1,
        x: 0,
        scale: 1,
        y: [0, -8, 0, 8, 0],
      }}
      transition={{
        opacity: { duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] },
        x: { duration: 1, delay, ease: [0.25, 0.46, 0.45, 0.94] },
        scale: { duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] },
        y: { duration: duration, repeat: Infinity, ease: "easeInOut", delay: delay + 1 },
      }}
    >
      <div className="floating-weather-station-image">
        <WeatherStationSVG />
      </div>
      <span className="floating-object-label">Weather Station</span>
    </motion.div>
  )
}

// Sensors component with smart box SVG - stationary, positioned next to panels
const FloatingSensors = ({ position, delay, mirrored }) => {
  return (
    <motion.div
      className={`floating-object floating-sensors sensors-stationary ${mirrored ? 'sensors-mirrored' : ''}`}
      style={{
        '--float-color': '#F97316',
        top: position.top,
        left: position.left,
        right: position.right,
        bottom: position.bottom,
      }}
      initial={{ opacity: 0, y: 150, scale: 0.5 }}
      animate={{
        opacity: 1,
        y: 0,
        scale: 1,
      }}
      transition={{
        opacity: { duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] },
        y: { duration: 1, delay, ease: [0.25, 0.46, 0.45, 0.94] },
        scale: { duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] },
      }}
    >
      <span className="floating-object-label sensor-label-top">Sensors</span>
      <div className="floating-sensors-image">
        <SmartSensorBoxSVG />
      </div>
    </motion.div>
  )
}

// Floating AI/ML component with robot SVG image
const FloatingAIML = ({ position, delay, duration }) => {
  const [hasEntered, setHasEntered] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setHasEntered(true), (delay + 1) * 1000)
    return () => clearTimeout(timer)
  }, [delay])

  return (
    <motion.div
      className="floating-object floating-aiml"
      style={{
        '--float-color': '#8B5CF6',
        top: position.top,
        left: position.left,
        right: position.right,
        bottom: position.bottom,
      }}
      initial={{ opacity: 0, x: -200, scale: 0.5 }}
      animate={hasEntered ? {
        opacity: 1,
        scale: 1,
        x: 0,
        y: [0, -12, 0, 12, 0],
      } : {
        opacity: 1,
        scale: 1,
        x: 0,
      }}
      transition={hasEntered ? {
        y: { duration: duration, repeat: Infinity, ease: "easeInOut" },
      } : {
        opacity: { duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] },
        scale: { duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] },
        x: { duration: 1, delay, ease: [0.25, 0.46, 0.45, 0.94] },
      }}
    >
      <div className="floating-aiml-image">
        <img src="/AI_ML.svg" alt="AI/ML" className="humanoid-robot-svg" />
      </div>
      <span className="floating-object-label">AI/ML</span>
    </motion.div>
  )
}

// Floating Satellite component with SVG
const FloatingSatellite = ({ position, delay, duration }) => {
  const [hasEntered, setHasEntered] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setHasEntered(true), (delay + 1) * 1000)
    return () => clearTimeout(timer)
  }, [delay])

  return (
    <motion.div
      className="floating-object floating-satellite"
      style={{
        '--float-color': '#8B5CF6',
        top: position.top,
        left: position.left,
        right: position.right,
        bottom: position.bottom,
      }}
      initial={{ opacity: 0, x: -150, y: -150, scale: 0.3, rotate: -30 }}
      animate={hasEntered ? {
        opacity: 1,
        scale: 1,
        x: 0,
        y: [0, -15, 0, 15, 0],
        rotate: [0, 3, 0, -3, 0],
      } : {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        rotate: 0,
      }}
      transition={hasEntered ? {
        y: { duration: duration, repeat: Infinity, ease: "easeInOut" },
        rotate: { duration: duration * 1.5, repeat: Infinity, ease: "easeInOut" },
      } : {
        opacity: { duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] },
        scale: { duration: 1, delay, ease: [0.25, 0.46, 0.45, 0.94] },
        x: { duration: 1, delay, ease: [0.25, 0.46, 0.45, 0.94] },
        y: { duration: 1, delay, ease: [0.25, 0.46, 0.45, 0.94] },
        rotate: { duration: 1, delay, ease: [0.25, 0.46, 0.45, 0.94] },
      }}
    >
      <div className="floating-satellite-image">
        <SatelliteSVG />
      </div>
      <span className="floating-object-label">Satellite</span>
    </motion.div>
  )
}

// Floating object component
const FloatingObject = ({ icon: Icon, label, position, delay, duration, color }) => {
  return (
    <motion.div
      className="floating-object"
      style={{
        '--float-color': color,
        top: position.top,
        left: position.left,
        right: position.right,
        bottom: position.bottom,
      }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{
        opacity: 1,
        scale: 1,
        y: [0, -15, 0, 15, 0],
        x: [0, 8, 0, -8, 0],
      }}
      transition={{
        opacity: { duration: 0.6, delay },
        scale: { duration: 0.6, delay },
        y: { duration: duration, repeat: Infinity, ease: "easeInOut", delay },
        x: { duration: duration * 1.3, repeat: Infinity, ease: "easeInOut", delay: delay + 0.5 },
      }}
    >
      <div className="floating-object-icon">
        <Icon size={28} />
      </div>
      <span className="floating-object-label">{label}</span>
    </motion.div>
  )
}

// Flying DJI Drone component with spotlight
const FlyingDrone = ({ scrollProgress, isNightMode, onCapture }) => {
  const [showFlash, setShowFlash] = useState(false)
  const [showSpotlight, setShowSpotlight] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [hasEntered, setHasEntered] = useState(false)
  const hasFlashedRef = useRef(false)

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 640)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Desktop: Trigger entrance animation on mount
  useEffect(() => {
    if (!isMobile) {
      const timer = setTimeout(() => setHasEntered(true), 300)
      return () => clearTimeout(timer)
    }
  }, [isMobile])

  // MOBILE: Drone appears from the right side when user starts scrolling
  const mobileDroneX = useTransform(scrollProgress, [0, 0.02, 0.15, 0.25], ['120%', '85%', '50%', '30%'])
  const mobileDroneY = useTransform(scrollProgress, [0, 0.02, 0.15, 0.25], ['5%', '15%', '55%', '70%'])
  const mobileDroneScale = useTransform(scrollProgress, [0, 0.02, 0.15, 0.25], [0.4, 0.6, 1, 1.2])
  const mobileDroneRotate = useTransform(scrollProgress, [0, 0.02, 0.1, 0.2], [-15, 0, -5, 5])
  const mobileDroneOpacity = useTransform(scrollProgress, [0, 0.005, 0.02], [0, 0.7, 1])

  // DESKTOP: Drone position based on scroll - flies from top right to over the solar panels
  const desktopDroneX = useTransform(scrollProgress, [0, 0.15, 0.25], ['85%', '50%', '30%'])
  const desktopDroneY = useTransform(scrollProgress, [0, 0.15, 0.25], ['15%', '55%', '70%'])
  const desktopDroneScale = useTransform(scrollProgress, [0, 0.15, 0.25], [0.6, 1, 1.2])
  const desktopDroneRotate = useTransform(scrollProgress, [0, 0.1, 0.2], [0, -5, 5])

  // Spotlight intensity increases in night mode
  const baseSpotlightOpacity = useTransform(scrollProgress, [0.08, 0.18], [0, 1])

  // Camera capture effect - only triggers once when drone reaches position
  useEffect(() => {
    const unsubscribe = scrollProgress.on('change', (value) => {
      // Trigger once at specific scroll point
      if (value > 0.145 && value < 0.155 && !hasFlashedRef.current) {
        hasFlashedRef.current = true
        setShowFlash(true)
        // Notify parent to activate thermal mode
        onCapture && onCapture(true)
        setTimeout(() => setShowFlash(false), 150)
        // Hide spotlight after capture
        setTimeout(() => setShowSpotlight(false), 400)
      }
      // Reset when scrolling back up
      if (value < 0.05) {
        hasFlashedRef.current = false
        setShowSpotlight(true)
        onCapture && onCapture(false)
      }
    })
    return () => unsubscribe()
  }, [scrollProgress, onCapture])

  // Choose mobile or desktop values
  const droneX = isMobile ? mobileDroneX : desktopDroneX
  const droneY = isMobile ? mobileDroneY : desktopDroneY
  const droneScale = isMobile ? mobileDroneScale : desktopDroneScale
  const droneRotate = isMobile ? mobileDroneRotate : desktopDroneRotate

  return (
    <motion.div
      className="flying-drone"
      style={isMobile ? {
        // Mobile: scroll-based positioning
        left: droneX,
        top: droneY,
        scale: droneScale,
        rotate: droneRotate,
        opacity: mobileDroneOpacity,
      } : {
        // Desktop: scroll-based positioning after entrance
        left: droneX,
        top: droneY,
        scale: droneScale,
        rotate: droneRotate,
      }}
      // Desktop entrance animation
      {...(!isMobile && {
        initial: { opacity: 0, x: 200, y: -100 },
        animate: hasEntered ? { opacity: 1, x: 0, y: 0 } : { opacity: 0, x: 200, y: -100 },
        transition: {
          duration: 1,
          delay: 0.2,
          ease: [0.25, 0.46, 0.45, 0.94],
        }
      })}
    >
      {/* Yellow spotlight beam - visible until capture, then fades out */}
      {showSpotlight && (
        <motion.div
          className="drone-spotlight"
          style={{ opacity: baseSpotlightOpacity }}
        />
      )}

      {/* Drone body with subtle animation */}
      <motion.div
        className="drone-body"
        animate={{
          y: [0, -3, 0, 3, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        {/* DJI Drone SVG - realistic silhouette */}
        <svg className="drone-svg" viewBox="0 0 120 80" fill="none">
          {/* Main body */}
          <ellipse cx="60" cy="40" rx="18" ry="12" fill="#2a2a2a"/>
          <ellipse cx="60" cy="38" rx="14" ry="8" fill="#3a3a3a"/>

          {/* Camera gimbal */}
          <circle cx="60" cy="48" r="6" fill="#1a1a1a"/>
          <circle cx="60" cy="48" r="4" fill="#0a0a0a"/>
          <circle cx="60" cy="48" r="2" fill="#4a90d9"/>

          {/* Front arms */}
          <path d="M45 35 L20 20" stroke="#2a2a2a" strokeWidth="4" strokeLinecap="round"/>
          <path d="M75 35 L100 20" stroke="#2a2a2a" strokeWidth="4" strokeLinecap="round"/>

          {/* Back arms */}
          <path d="M45 45 L20 60" stroke="#2a2a2a" strokeWidth="4" strokeLinecap="round"/>
          <path d="M75 45 L100 60" stroke="#2a2a2a" strokeWidth="4" strokeLinecap="round"/>

          {/* Propeller mounts */}
          <circle cx="20" cy="20" r="6" fill="#3a3a3a"/>
          <circle cx="100" cy="20" r="6" fill="#3a3a3a"/>
          <circle cx="20" cy="60" r="6" fill="#3a3a3a"/>
          <circle cx="100" cy="60" r="6" fill="#3a3a3a"/>

          {/* Spinning propellers */}
          <ellipse cx="20" cy="20" rx="12" ry="3" fill="rgba(100,100,100,0.4)" className="propeller"/>
          <ellipse cx="100" cy="20" rx="12" ry="3" fill="rgba(100,100,100,0.4)" className="propeller"/>
          <ellipse cx="20" cy="60" rx="12" ry="3" fill="rgba(100,100,100,0.4)" className="propeller"/>
          <ellipse cx="100" cy="60" rx="12" ry="3" fill="rgba(100,100,100,0.4)" className="propeller"/>

          {/* LED lights */}
          <circle cx="20" cy="20" r="2" fill="#ff3333" className="drone-led"/>
          <circle cx="100" cy="20" r="2" fill="#33ff33" className="drone-led"/>
        </svg>
      </motion.div>

      {/* Camera capture effect - localized flash */}
      {showFlash && (
        <>
          <div className="drone-capture-flash" />
          <div className="drone-capture-indicator">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M12 2v2M12 20v2M2 12h2M20 12h2" />
            </svg>
          </div>
        </>
      )}
    </motion.div>
  )
}

// Realistic solar panel with photovoltaic cells - supports thermal imaging mode
const SolarPanel = ({ delay, glowIntensity, isThermal, hotspotPattern }) => {
  // Generate random hotspot cells for this panel (consistent per panel)
  const hotspotCells = hotspotPattern || []

  return (
    <motion.div
      className={`solar-panel ${isThermal ? 'thermal-mode' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
    >
      <motion.div
        className="solar-panel-glow"
        style={{ opacity: glowIntensity }}
      />
      <div className="solar-panel-frame">
        {/* Aluminum frame */}
        <div className="solar-panel-border" />
        {/* Cell grid - 6x10 cells like real panels */}
        <div className="solar-panel-cells">
          {[...Array(60)].map((_, i) => {
            // Determine thermal intensity for each cell
            const isHotspot = hotspotCells.includes(i)
            const isWarm = hotspotCells.includes(i - 1) || hotspotCells.includes(i + 1) ||
                          hotspotCells.includes(i - 10) || hotspotCells.includes(i + 10)

            return (
              <div
                key={i}
                className={`solar-cell ${isThermal ? 'thermal' : ''} ${isHotspot ? 'hotspot' : ''} ${isWarm && !isHotspot ? 'warm' : ''}`}
              >
                <div className="cell-busbar cell-busbar-v" />
                <div className="cell-busbar cell-busbar-h" />
              </div>
            )
          })}
        </div>
        {/* Glass reflection */}
        <div className="solar-panel-glass" />
      </div>
    </motion.div>
  )
}

// Generate random hotspot patterns for panels
const generateHotspotPattern = (panelIndex) => {
  // Seeded random based on panel index for consistency
  const seed = panelIndex * 7 + 13
  const hotspots = []
  const numHotspots = 2 + (seed % 4) // 2-5 hotspots per panel

  for (let i = 0; i < numHotspots; i++) {
    const cellIndex = ((seed * (i + 1) * 17) % 60)
    hotspots.push(cellIndex)
    // Add adjacent cells for larger hotspot areas
    if (cellIndex < 50) hotspots.push(cellIndex + 10)
    if (cellIndex % 10 < 9) hotspots.push(cellIndex + 1)
  }

  return [...new Set(hotspots)] // Remove duplicates
}

export default function HeroSection() {
  const containerRef = useRef(null)
  const [isThermalMode, setIsThermalMode] = useState(false)

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  })

  // Very immediate day to night transition - starts almost immediately
  const bgOpacity = useTransform(scrollYProgress, [0.02, 0.15], [0, 1])
  const textY = useTransform(scrollYProgress, [0, 1], [0, -100])
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0])
  const objectsOpacity = useTransform(scrollYProgress, [0, 0.12], [1, 0])
  const objectsScale = useTransform(scrollYProgress, [0, 0.12], [1, 0.8])

  // Stats box fades out faster than other content
  const statsOpacity = useTransform(scrollYProgress, [0, 0.08], [1, 0])

  // Panel glow increases as it gets darker (night mode)
  const panelGlow = useTransform(scrollYProgress, [0.02, 0.15], [0, 1])

  // Back and middle rows fade in on scroll (not visible on page load)
  const backRowOpacity = useTransform(scrollYProgress, [0.02, 0.08], [0, 0.08])
  const middleRowOpacity = useTransform(scrollYProgress, [0.02, 0.06], [0, 0.12])

  // Handle drone capture - triggers thermal mode
  const handleDroneCapture = (captured) => {
    setIsThermalMode(captured)
  }

  // Floating objects configuration - all now use custom SVG components

  return (
    <section className="hero hero-light" ref={containerRef}>
      {/* Light background (day) */}
      <div className="hero-bg-light" />

      {/* Dark background overlay (night) - more immediate transition */}
      <motion.div
        className="hero-bg-dark"
        style={{ opacity: bgOpacity }}
      />

      {/* Floating objects around content */}
      <motion.div
        className="floating-objects-container"
        style={{ opacity: objectsOpacity, scale: objectsScale }}
      >
        {/* Real Satellite Image */}
        <FloatingSatellite
          position={{ top: '15%', left: '8%' }}
          delay={0.1}
          duration={8}
        />

        {/* Humanoid AI/ML */}
        <FloatingAIML
          position={{ top: '58%', left: '6%' }}
          delay={0.5}
          duration={5}
        />

        {/* Weather Station - positioned same level as AI/ML */}
        <FloatingWeatherStation
          position={{ top: '58%', right: '8%' }}
          delay={0.4}
          duration={9}
        />

        {/* Smart Sensor Box - positioned above the rightmost panels */}
        <FloatingSensors
          position={{ bottom: '55px', right: '21%' }}
          delay={0.6}
        />

        {/* Smart Sensor Box - mirrored on left side, above leftmost panels */}
        <FloatingSensors
          position={{ bottom: '55px', left: '21%' }}
          delay={0.7}
          mirrored
        />

        {/* Connection lines between objects */}
        <svg className="connection-lines" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="line-grad-1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.3" />
            </linearGradient>
            <linearGradient id="line-grad-2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#0EA5E9" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#10B981" stopOpacity="0.3" />
            </linearGradient>
          </defs>
          {/* Curved connection paths */}
          <motion.path
            d="M15,25 Q50,10 85,30"
            fill="none"
            stroke="url(#line-grad-1)"
            strokeWidth="0.15"
            strokeDasharray="2 2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, delay: 1 }}
          />
          <motion.path
            d="M10,70 Q50,90 90,75"
            fill="none"
            stroke="url(#line-grad-2)"
            strokeWidth="0.15"
            strokeDasharray="2 2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, delay: 1.5 }}
          />
          {/* Orbiting ellipse */}
          <motion.ellipse
            cx="50"
            cy="50"
            rx="40"
            ry="25"
            fill="none"
            stroke="rgba(139, 92, 246, 0.1)"
            strokeWidth="0.1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
          />
        </svg>

        {/* Orbiting particle */}
        <motion.div
          className="orbiting-particle"
          animate={{
            rotate: 360,
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <div className="particle-dot" />
        </motion.div>
      </motion.div>

      {/* Flying DJI Drone */}
      <FlyingDrone scrollProgress={scrollYProgress} isNightMode={bgOpacity} onCapture={handleDroneCapture} />

      {/* Main content - centered */}
      <motion.div className="hero-content" style={{ y: textY, opacity }}>
        <motion.h1
          className="hero-title"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          See everything.{' '}
          <GradientText gradient="solar">Miss nothing.</GradientText>
        </motion.h1>

        <motion.p
          className="hero-subtitle"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          Unifying ground sensors, aerial thermal and EL imaging, and satellite data
          into one complete solar monitoring solution.
        </motion.p>

        <motion.div
          className="hero-actions"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <GlowButton href="/demo" variant="solar" size="large">
            Request Demo
          </GlowButton>
          <GlowButton href="#features" variant="outline" size="large">
            Explore Platform
          </GlowButton>
        </motion.div>

        <motion.div
          className="hero-stats"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          style={{ opacity: statsOpacity }}
        >
          <div className="hero-stat">
            <AnimatedCounter value={516} className="hero-stat-number" />
            <span className="hero-stat-label">APIs</span>
          </div>
          <div className="hero-stat-divider" />
          <div className="hero-stat">
            <AnimatedCounter value={61} className="hero-stat-number" />
            <span className="hero-stat-label">Mobile Screens</span>
          </div>
          <div className="hero-stat-divider" />
          <div className="hero-stat">
            <AnimatedCounter value={6} className="hero-stat-number" />
            <span className="hero-stat-label">ML Models</span>
          </div>
        </motion.div>

      </motion.div>

      {/* Solar Farm at bottom */}
      <div className="hero-solar-farm">
        <motion.div
          className="solar-farm-glow-overlay"
          style={{ opacity: panelGlow }}
        />
        <div className="solar-farm-ground" />
        <div className={`solar-farm-panels ${isThermalMode ? 'thermal-active' : ''}`}>
          {/* Row 1 - Back (fades in on scroll) */}
          <motion.div className="solar-farm-row solar-farm-row-back" style={{ opacity: backRowOpacity }}>
            {[...Array(8)].map((_, i) => (
              <SolarPanel
                key={`back-${i}`}
                delay={0.1 + i * 0.05}
                glowIntensity={panelGlow}
                isThermal={isThermalMode}
                hotspotPattern={generateHotspotPattern(i)}
              />
            ))}
          </motion.div>
          {/* Row 2 - Middle (fades in on scroll) */}
          <motion.div className="solar-farm-row solar-farm-row-middle" style={{ opacity: middleRowOpacity }}>
            {[...Array(10)].map((_, i) => (
              <SolarPanel
                key={`mid-${i}`}
                delay={0.15 + i * 0.05}
                glowIntensity={panelGlow}
                isThermal={isThermalMode}
                hotspotPattern={generateHotspotPattern(i + 8)}
              />
            ))}
          </motion.div>
          {/* Row 3 - Front */}
          <div className="solar-farm-row solar-farm-row-front">
            {[...Array(12)].map((_, i) => (
              <SolarPanel
                key={`front-${i}`}
                delay={0.2 + i * 0.05}
                glowIntensity={panelGlow}
                isThermal={isThermalMode}
                hotspotPattern={generateHotspotPattern(i + 18)}
              />
            ))}
          </div>
        </div>
        {/* Stars that appear at night */}
        <motion.div
          className="hero-stars"
          style={{ opacity: panelGlow }}
        >
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="star"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 60}%`,
                animationDelay: `${Math.random() * 3}s`,
                width: `${2 + Math.random() * 2}px`,
                height: `${2 + Math.random() * 2}px`,
              }}
            />
          ))}
        </motion.div>
      </div>

    </section>
  )
}
